import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import CustomTooltip from "../../../../../components/CustomTooltip";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { AssetClass } from "../../../../../utils/constants";
import { formatNumberCompact } from "../../../../../utils/formatters";
import AssetBusinessValue from "./AssetBusinessValue";
import AssetDescription from "./AssetDescription";
import AssetNameInput from "./AssetNameInput";
import AssetTypeSelect from "./AssetTypeSelect";

const AssetDetailsBox = ({ setFormData, formData, assetToEdit = null }) => {
  const assetDetails = formData?.stepAssetDetails?.asset || {};
  const [openEdit, setOpenEdit] = useState(false);
  const colors = useColors();
  const disabled = assetToEdit?.asset?.cls === AssetClass.SMART;
  const { businessValueMode } = useContext(EnvFeatureFlagContext);
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid " + colors.divider,
        borderRadius: "10px",
        backgroundColor: colors.common_white,
        width: "100%",
        gap: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color={colors.primary_main}>
          Asset Details
        </Typography>
        <IconButton size="small" onClick={() => setOpenEdit(!openEdit)}>
          {openEdit ? (
            <CheckIcon
              sx={{ color: colors.grey_700, width: "18px", height: "18px" }}
            />
          ) : (
            <CustomTooltip
              title={disabled ? "Edit business value" : "Edit"}
              placement="top"
            >
              <EditIcon
                sx={{ color: colors.grey_700, width: "18px", height: "18px" }}
              />
            </CustomTooltip>
          )}
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {openEdit ? (
            <FormControl variant="standard" fullWidth>
              <AssetNameInput
                formData={formData}
                setFormData={setFormData}
                assetToEdit={assetToEdit}
              />
            </FormControl>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" color={colors.text_secondary}>
                Asset Name
              </Typography>
              <Typography variant="body2">
                {assetDetails.assetName || ""}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ flexGrow: 1 }}>
          {openEdit ? (
            <FormControl fullWidth>
              <AssetTypeSelect
                setFormData={setFormData}
                formData={formData}
                disabled={disabled}
              />
            </FormControl>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" color={colors.text_secondary}>
                Asset Category
              </Typography>
              <Typography variant="body2">
                {assetDetails.assetType || "0"}
              </Typography>
            </Box>
          )}
        </Box>{" "}
        {businessValueMode && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ flexGrow: 1 }}>
              {openEdit ? (
                <FormControl variant="standard" fullWidth>
                  <AssetBusinessValue
                    formData={formData}
                    setFormData={setFormData}
                    showHelperText={false}
                  />
                </FormControl>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="caption" color={colors.text_secondary}>
                    Business Value
                  </Typography>
                  <Typography variant="body2">
                    ${formatNumberCompact(assetDetails.businessValue || 0)}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Fix for infinite render - only show description section if we're editing or there's a description */}
      {(openEdit || assetDetails.reason) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mt: 1 }}>
            {openEdit ? (
              <FormControl variant="standard" fullWidth>
                <AssetDescription
                  formData={formData}
                  setFormData={setFormData}
                  disabled={disabled}
                />
              </FormControl>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="caption" color={colors.text_secondary}>
                  Description
                </Typography>
                <Typography variant="body2">
                  {assetDetails.reason ||
                    "A short description why these entities are grouped as a critical asset"}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AssetDetailsBox;
